import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import {
  HomeOutlined, BookOutlined, TrophyOutlined, ExperimentOutlined,
  ReadOutlined, DownloadOutlined, DollarOutlined, FormOutlined,
  PhoneOutlined, CloseOutlined, ThunderboltOutlined, DownOutlined, RightOutlined
} from "@ant-design/icons";

export default function Sidebar({ mobileOpen, onClose }) {
  const location = useLocation();
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/course/getAll`);
        // The API returns { data: [...], total: ..., totalPage: ... }
        setCourses(res?.data?.data || res?.data || []);
      } catch (e) {
        console.error("Failed to fetch courses for sidebar:", e);
      }
    };
    fetchCourses();
  }, []);

  const menu = [
    { label: "Trang Chủ", to: "/", icon: <HomeOutlined /> },
    { label: "Khóa Học", to: "/topic", icon: <BookOutlined />, hasSubmenu: true },
    { label: "Thi Đấu", to: "/competition", icon: <ThunderboltOutlined />, badge: "New" },
    { label: "Phương Pháp", to: "/method", icon: <ExperimentOutlined /> },
    { label: "Cách Học Chi Tiết", to: "/how", icon: <ReadOutlined /> },
    { label: "Hướng Dẫn Tải App", to: "/download", icon: <DownloadOutlined /> },
    { label: "Bảng Giá", to: "/pricing", icon: <DollarOutlined /> },
    { label: "Blog", to: "/blog", icon: <FormOutlined /> },
    { label: "Liên Hệ", to: "/contact", icon: <PhoneOutlined /> },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const sidebarContent = (
    <div className="px-2 py-4">
      <div className="px-4 pb-4 mb-2 border-b border-blue-100 flex items-center justify-between">
        <h3 className="text-lg font-bold text-[#1e90ff]">VuaLingo</h3>
        <button
          onClick={onClose}
          className="md:hidden w-8 h-8 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-500"
        >
          <CloseOutlined />
        </button>
      </div>
      <nav>
        <ul className="space-y-1">
          {menu.map((m) => (
            <li key={m.to}>
              {m.hasSubmenu ? (
                <>
                  {/* Khóa Học — click toggles dropdown */}
                  <div
                    onClick={() => setCoursesOpen(!coursesOpen)}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg transition-all cursor-pointer ${
                      isActive(m.to)
                        ? "bg-blue-500 text-white shadow-md shadow-blue-500/25"
                        : "text-gray-700 hover:bg-white hover:shadow"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-lg ${isActive(m.to) ? "text-white" : "text-gray-400"}`}>{m.icon}</span>
                      <span className="font-medium">{m.label}</span>
                    </div>
                    <span className={`text-xs transition-transform duration-200 ${coursesOpen ? 'rotate-0' : ''}`}>
                      {coursesOpen ? <DownOutlined /> : <RightOutlined />}
                    </span>
                  </div>
                  {/* Submenu — courses list */}
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    coursesOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <ul className="ml-4 mt-1 space-y-0.5 border-l-2 border-blue-200 pl-3">
                      {/* View all link */}
                      <li>
                        <Link
                          to="/topic"
                          onClick={onClose}
                          className={`block px-3 py-2 rounded-lg text-sm transition-all ${
                            location.pathname === '/topic'
                              ? 'bg-blue-50 text-blue-600 font-semibold'
                              : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                          }`}
                        >
                          📚 Xem tất cả
                        </Link>
                      </li>
                      {Array.isArray(courses) && courses.map((course) => (
                        <li key={course.id || course.slug}>
                          <Link
                            to={`/lesson/${course.slug}`}
                            onClick={onClose}
                            className={`block px-3 py-2 rounded-lg text-sm transition-all truncate ${
                              location.pathname === `/lesson/${course.slug}`
                                ? 'bg-blue-50 text-blue-600 font-semibold'
                                : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                            }`}
                          >
                            📖 {course.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <Link
                  to={m.to}
                  onClick={onClose}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                    isActive(m.to)
                      ? "bg-blue-500 text-white shadow-md shadow-blue-500/25"
                      : "text-gray-700 hover:bg-white hover:shadow"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`text-lg ${isActive(m.to) ? "text-white" : "text-gray-400"}`}>{m.icon}</span>
                    <span className="font-medium">{m.label}</span>
                  </div>
                  {m.badge && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      isActive(m.to) ? "bg-white/20 text-white" : "bg-red-500 text-white"
                    }`}>{m.badge}</span>
                  )}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden md:block w-64 bg-[#f0f7fb] min-h-screen sticky top-[56px] shadow-inner flex-shrink-0">
        {sidebarContent}
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#f0f7fb] shadow-2xl overflow-y-auto animate-slideIn">
            {sidebarContent}
          </aside>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slideIn {
          animation: slideIn 0.25s ease-out;
        }
      `}</style>
    </>
  );
}
